/**
 * Modal Utility
 * A lightweight, customizable modal system
 * Z-index conflicts are now handled by CSS in _modal.scss
 */

export const modalUtility = {
    initialized: false,
    activeModal: null,
    backdrop: null,
    
    /**
     * Initialize the modal utility
     * Creates overlay and sets up event listeners
     */
    init() {
        if (this.initialized) return;
        
        // Clean up any stray backdrops or overlays from previous sessions
        this.cleanupOverlays();
        
        // Add event delegation for modal triggers
        document.body.addEventListener('click', (e) => {
            // Check if the click was on the backdrop
            if (this.backdrop && e.target === this.backdrop) {
                this.closeModal();
                return;
            }
            
            const trigger = e.target.closest('[data-custom-modal-target]');
            if (trigger) {
                e.preventDefault();
                const targetId = trigger.getAttribute('data-custom-modal-target');
                const modal = document.getElementById(targetId);
                if (modal) {
                    this.openModal(modal, trigger);
                }
            }
            
            // Close modal when close button is clicked
            if (e.target.closest('.modal__close') || e.target.closest('.btn-close')) {
                this.closeModal();
            }
        });
        
        // Add keyboard event listener for ESC key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.activeModal) {
                this.closeModal();
            }
        });
        
        this.initialized = true;
    },
    
    /**
     * Clean up any stray overlays, backdrops, or modal-related DOM elements
     */
    cleanupOverlays() {
        // Remove any orphaned backdrops
        const orphanedBackdrops = document.querySelectorAll('#custom-modal-backdrop, .modal-backdrop');
        orphanedBackdrops.forEach(backdrop => backdrop.remove());
        
        // Remove modal-open class from body if no modals are open
        if (!document.querySelector('.modal.show')) {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = '';
        }
        
        // Reset internal state
        this.backdrop = null;
        this.activeModal = null;
    },
    
    /**
     * Set up a modal element with options
     * @param {HTMLElement} modalElement - The modal DOM element
     * @param {Object} options - Configuration options
     * @returns {HTMLElement} - The configured modal element
     */
    setupModal(modalElement, options = {}) {
        // Store options on the modal element
        modalElement._modalOptions = {
            onOpen: options.onOpen || null,
            onClose: options.onClose || null,
            beforeOpen: options.beforeOpen || null,
            beforeClose: options.beforeClose || null
        };
        
        // Add custom styles to prevent bootstrap from controlling this modal
        modalElement.classList.add('custom-modal-enabled');
        
        // Find the existing modal dialog for styling
        const modalDialog = modalElement.querySelector('.modal-dialog');
        if (modalDialog) {
            modalDialog.classList.add('custom-modal-dialog');
        }
        
        // Add a click handler to the modal element itself that only triggers
        // when clicking directly on the modal background (not any children)
        modalElement.addEventListener('mousedown', (e) => {
            if (e.target === modalElement) {
                this.closeModal();
            }
        });
        
        return modalElement;
    },
    
    /**
     * Create or get the backdrop element
     * @returns {HTMLElement} - The backdrop element
     */
    createBackdrop() {
        // Create a new backdrop element
        const backdrop = document.createElement('div');
        backdrop.id = 'custom-modal-backdrop';
        backdrop.className = 'modal-backdrop fade show';
        
        // Using a direct click handler instead of event delegation
        const self = this;
        backdrop.onclick = function(e) {
            if (e.target === self.backdrop) {
                self.closeModal();
            }
        };
        
        // Store the reference
        this.backdrop = backdrop;
        
        // Add to the DOM
        document.body.appendChild(backdrop);
        
        return backdrop;
    },
    
    /**
     * Open a modal
     * @param {HTMLElement} modalElement - The modal to open
     * @param {HTMLElement} triggerElement - The element that triggered the modal
     */
    openModal(modalElement, triggerElement) {
        // Run beforeOpen callback if exists
        if (modalElement._modalOptions?.beforeOpen) {
            const shouldContinue = modalElement._modalOptions.beforeOpen(modalElement, triggerElement);
            if (shouldContinue === false) return;
        }
        
        this.activeModal = modalElement;
        
        // Store trigger element info
        if (triggerElement) {
            modalElement.setAttribute('data-trigger-id', triggerElement.id || '');
            modalElement.relatedTarget = triggerElement;
        }
        
        // Show modal and add classes for styling
        modalElement.style.display = 'block';
        modalElement.classList.add('show');
        
        // Add modal-open class to body
        document.body.classList.add('modal-open');
        document.body.style.overflow = 'hidden'; // Prevent scrolling
        
        // Create and show the backdrop
        this.createBackdrop();
        
        // Run onOpen callback if exists
        if (modalElement._modalOptions?.onOpen) {
            setTimeout(() => {
                modalElement._modalOptions.onOpen(modalElement, triggerElement);
            }, 50);
        }
    },
    
    /**
     * Close the currently active modal
     */
    closeModal() {
        if (!this.activeModal) return;
        
        // Run beforeClose callback if exists
        if (this.activeModal._modalOptions?.beforeClose) {
            const shouldContinue = this.activeModal._modalOptions.beforeClose(this.activeModal);
            if (shouldContinue === false) return;
        }
        
        // Hide modal
        this.activeModal.style.display = 'none';
        this.activeModal.classList.remove('show');
        
        // Remove modal-open class from body
        document.body.classList.remove('modal-open');
        document.body.style.overflow = ''; // Restore scrolling
        
        // Remove the backdrop
        if (this.backdrop && document.body.contains(this.backdrop)) {
            this.backdrop.remove();
            this.backdrop = null;
        }
        
        // Also check for any orphaned backdrops that might be stuck
        const orphanedBackdrops = document.querySelectorAll('#custom-modal-backdrop, .modal-backdrop');
        orphanedBackdrops.forEach(backdrop => {
            backdrop.remove();
        });
        
        // Run onClose callback if exists
        if (this.activeModal._modalOptions?.onClose) {
            this.activeModal._modalOptions.onClose(this.activeModal);
        }
        
        this.activeModal = null;
    },
    
    /**
     * Get the currently active modal
     * @returns {HTMLElement|null} The active modal or null
     */
    getCurrentModal() {
        return this.activeModal;
    }
    
}; 